import type { Sport } from '../types/core';

export interface StatCardLine {
  /** Stat key — either a raw SPORT_STAT_KEYS key or a calculated key from statCalculations.ts. */
  key: string;
  label: string;
  /** True if this comes from getCalculatedStats() rather than the raw season totals. */
  calculated?: boolean;
}

export interface PositionStatProfile {
  label: string;
  lines: StatCardLine[];
}

/**
 * Position-aware "trading card" stat lines for the individual persona's
 * Dashboard profile (sc-336 follow-up). Deliberately data, not derived from
 * SPORT_STAT_KEYS automatically — a QB's card and a DB's card pull from the
 * same key catalog but should show different subsets, which is a curation
 * call, not something to infer generically.
 */
interface SportStatCardConfig {
  /** Keyed by the position abbreviations in positionHierarchy.ts (e.g. 'QB', 'P'). */
  positions: Record<string, PositionStatProfile>;
  /** Used when the player's position isn't in `positions`, or the sport has none set. */
  default: PositionStatProfile;
}

const BATTING_LINES: StatCardLine[] = [
  { key: 'AVG', label: 'AVG', calculated: true },
  { key: 'OBP', label: 'OBP', calculated: true },
  { key: 'SLG', label: 'SLG', calculated: true },
  { key: 'HR', label: 'HR' },
  { key: 'RBI', label: 'RBI' },
  { key: 'R', label: 'R' },
  { key: 'SB', label: 'SB' },
];

const PITCHING_LINES: StatCardLine[] = [
  { key: 'ERA', label: 'ERA', calculated: true },
  { key: 'WHIP', label: 'WHIP', calculated: true },
  { key: 'K', label: 'K' },
  { key: 'W', label: 'W' },
  { key: 'L', label: 'L' },
  { key: 'SV', label: 'SV' },
];

const BASEBALL_CONFIG: SportStatCardConfig = {
  positions: {
    // A pitcher who also bats (no DH) gets both lines, per how youth/HS ball usually works.
    P: { label: 'Pitcher', lines: [...PITCHING_LINES, ...BATTING_LINES] },
  },
  default: { label: 'Batting', lines: BATTING_LINES },
};

const FOOTBALL_CONFIG: SportStatCardConfig = {
  positions: {
    QB: {
      label: 'Quarterback',
      lines: [
        { key: 'PassYds', label: 'Pass Yds' },
        { key: 'CompPct', label: 'Comp %', calculated: true },
        { key: 'PassTD', label: 'Pass TD' },
        { key: 'PassInt', label: 'INT' },
        { key: 'RushYds', label: 'Rush Yds' },
        { key: 'RushTD', label: 'Rush TD' },
      ],
    },
    RB: {
      label: 'Running Back',
      lines: [
        { key: 'RushYds', label: 'Rush Yds' },
        { key: 'YPC', label: 'YPC', calculated: true },
        { key: 'RushTD', label: 'Rush TD' },
        { key: 'Rec', label: 'Rec' },
        { key: 'RecYds', label: 'Rec Yds' },
      ],
    },
    WR: {
      label: 'Wide Receiver',
      lines: [
        { key: 'Rec', label: 'Rec' },
        { key: 'RecYds', label: 'Rec Yds' },
        { key: 'YPR', label: 'YPR', calculated: true },
        { key: 'RecTD', label: 'Rec TD' },
      ],
    },
    TE: {
      label: 'Tight End',
      lines: [
        { key: 'Rec', label: 'Rec' },
        { key: 'RecYds', label: 'Rec Yds' },
        { key: 'YPR', label: 'YPR', calculated: true },
        { key: 'RecTD', label: 'Rec TD' },
      ],
    },
    DL: {
      label: 'Defensive Line',
      lines: [
        { key: 'Tackles', label: 'Tackles' },
        { key: 'Sacks', label: 'Sacks' },
        { key: 'TFL', label: 'TFL' },
        { key: 'FF', label: 'FF' },
      ],
    },
    LB: {
      label: 'Linebacker',
      lines: [
        { key: 'Tackles', label: 'Tackles' },
        { key: 'Sacks', label: 'Sacks' },
        { key: 'TFL', label: 'TFL' },
        { key: 'Int', label: 'INT' },
      ],
    },
    DB: {
      label: 'Defensive Back',
      lines: [
        { key: 'Tackles', label: 'Tackles' },
        { key: 'Int', label: 'INT' },
        { key: 'PassDef', label: 'Pass Def' },
        { key: 'FF', label: 'FF' },
      ],
    },
    K: { label: 'Kicker', lines: [{ key: 'FG', label: 'FG' }, { key: 'XP', label: 'XP' }] },
  },
  // OL, P, LS, KR, PR have no schema-tracked individual stats today.
  default: { label: 'Overview', lines: [{ key: 'TD', label: 'TD' }, { key: 'Tackles', label: 'Tackles' }] },
};

const BASKETBALL_CONFIG: SportStatCardConfig = {
  positions: {},
  default: {
    label: 'Overview',
    lines: [
      { key: 'Points', label: 'PTS' },
      { key: 'Rebounds', label: 'REB' },
      { key: 'Assists', label: 'AST' },
      { key: 'Steals', label: 'STL' },
      { key: 'Blocks', label: 'BLK' },
      { key: 'FG%', label: 'FG%', calculated: true },
    ],
  },
};

const SOCCER_CONFIG: SportStatCardConfig = {
  positions: {
    GK: { label: 'Goalkeeper', lines: [{ key: 'Saves', label: 'Saves' }] },
  },
  default: {
    label: 'Overview',
    lines: [
      { key: 'Goals', label: 'Goals' },
      { key: 'Assists', label: 'Assists' },
      { key: 'Shots', label: 'Shots' },
      { key: 'ShotsOnGoal', label: 'SOG' },
    ],
  },
};

const HOCKEY_CONFIG: SportStatCardConfig = {
  positions: {
    G: { label: 'Goalie', lines: [{ key: 'Saves', label: 'Saves' }] },
  },
  default: {
    label: 'Overview',
    lines: [
      { key: 'Goals', label: 'Goals' },
      { key: 'Assists', label: 'Assists' },
      { key: 'Points', label: 'Points' },
      { key: 'Shots', label: 'Shots' },
    ],
  },
};

const VOLLEYBALL_CONFIG: SportStatCardConfig = {
  positions: {
    S: { label: 'Setter', lines: [{ key: 'Assists', label: 'Assists' }, { key: 'Aces', label: 'Aces' }, { key: 'Digs', label: 'Digs' }] },
    L: { label: 'Libero', lines: [{ key: 'Digs', label: 'Digs' }, { key: 'Aces', label: 'Aces' }] },
    DS: { label: 'Defensive Specialist', lines: [{ key: 'Digs', label: 'Digs' }, { key: 'Aces', label: 'Aces' }] },
  },
  default: {
    label: 'Overview',
    lines: [
      { key: 'Kills', label: 'Kills' },
      { key: 'Blocks', label: 'Blocks' },
      { key: 'Aces', label: 'Aces' },
      { key: 'Digs', label: 'Digs' },
    ],
  },
};

const LACROSSE_CONFIG: SportStatCardConfig = {
  positions: {
    G: { label: 'Goalie', lines: [{ key: 'Saves', label: 'Saves' }] },
    D: { label: 'Defense', lines: [{ key: 'GroundBalls', label: 'GB' }, { key: 'Turnovers', label: 'TO' }] },
    LSM: { label: 'Long Stick Mid', lines: [{ key: 'GroundBalls', label: 'GB' }, { key: 'Turnovers', label: 'TO' }] },
    SSDM: { label: 'Short Stick D-Mid', lines: [{ key: 'GroundBalls', label: 'GB' }, { key: 'Turnovers', label: 'TO' }] },
  },
  default: {
    label: 'Overview',
    lines: [
      { key: 'Goals', label: 'Goals' },
      { key: 'Assists', label: 'Assists' },
      { key: 'Points', label: 'Points' },
      { key: 'GroundBalls', label: 'GB' },
    ],
  },
};

const TENNIS_CONFIG: SportStatCardConfig = {
  // Tennis has no positions that change which stats matter — ladder slot is match-level, not a stat category.
  positions: {},
  default: {
    label: 'Overview',
    lines: [
      { key: 'Aces', label: 'Aces' },
      { key: 'Winners', label: 'Winners' },
      { key: 'UnforcedErrors', label: 'UE' },
      { key: 'SetsWon', label: 'Sets Won' },
    ],
  },
};

const SPORT_STAT_CARD_CONFIG: Partial<Record<Sport, SportStatCardConfig>> = {
  Baseball: BASEBALL_CONFIG,
  Softball: BASEBALL_CONFIG,
  Football: FOOTBALL_CONFIG,
  FlagFootball: FOOTBALL_CONFIG,
  Basketball: BASKETBALL_CONFIG,
  Soccer: SOCCER_CONFIG,
  Hockey: HOCKEY_CONFIG,
  Volleyball: VOLLEYBALL_CONFIG,
  Lacrosse: LACROSSE_CONFIG,
  Tennis: TENNIS_CONFIG,
};

/** The stat lines to show on a "trading card" for this sport + position (position may be undefined/unrecognized). */
export function getStatCardProfile(sport: Sport, position: string | undefined | null): PositionStatProfile {
  const config = SPORT_STAT_CARD_CONFIG[sport];
  if (!config) return { label: 'Overview', lines: [] };
  const byPosition = position ? config.positions[position] : undefined;
  return byPosition ?? config.default;
}
